import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, BarChart3, Calculator, TrendingUp, Zap, Target } from 'lucide-react';

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const navigationCards = [
    {
      id: 'database',
      title: 'Gestione Database',
      description: 'Carica, gestisci e organizza i tuoi database di gap trading',
      icon: Database,
      route: '/manage-databases',
      color: 'from-blue-500 to-blue-600',
      hoverColor: 'hover:from-blue-600 hover:to-blue-700',
      features: ['Carica file Excel', 'Gestisci database esistenti', 'Unisci dati', 'Esporta analisi']
    },
    {
      id: 'dashboard',
      title: 'Dashboard Principale',
      description: 'Analizza i gap con KPI avanzati, filtri e insights di trading',
      icon: BarChart3,
      route: '/dashboard',
      color: 'from-green-500 to-green-600',
      hoverColor: 'hover:from-green-600 hover:to-green-700',
      features: ['KPI Dashboard', 'Filtri avanzati', 'Analisi gap', 'Insights trading']
    },
    {
      id: 'backtester',
      title: 'Backtester',
      description: 'Testa e ottimizza le tue strategie di trading sui dati storici',
      icon: Calculator,
      route: '/backtesting',
      color: 'from-orange-500 to-red-600',
      hoverColor: 'hover:from-orange-600 hover:to-red-700',
      features: ['Test strategie storiche', 'Ottimizzazione parametri', 'Report dettagliati', 'Export risultati']
    }
  ];

  const handleNavigation = (route: string) => {
    navigate(route);
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
      {/* Header */}
      <div className="text-center mb-8 sm:mb-16">
        <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-6">
          <div className="p-3 sm:p-4 bg-blue-400/10 rounded-2xl">
            <TrendingUp className="h-8 w-8 sm:h-12 sm:w-12 text-blue-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white text-center sm:text-left">
            The Edge Dashboard
          </h1>
        </div>
        <p className="text-base sm:text-xl lg:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
          Piattaforma professionale per l'analisi avanzata dei gap di trading. 
          Trasforma i tuoi dati in insights azionabili con strumenti di analisi predittiva.
        </p>
        
        {/* Stats Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 mt-6 sm:mt-8 p-4 sm:p-6 bg-gray-800/30 rounded-2xl border border-gray-700 max-w-4xl mx-auto">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Zap className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
              <p className="text-lg sm:text-2xl font-bold text-white">Real-time</p>
            </div>
            <p className="text-xs sm:text-sm text-gray-400">Analisi in tempo reale</p>
          </div>
          <div className="hidden sm:block h-8 w-px bg-gray-600"></div>
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Target className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
              <p className="text-lg sm:text-2xl font-bold text-white">Forecast</p>
            </div>
            <p className="text-xs sm:text-sm text-gray-400">Algoritmi predittivi</p>
          </div>
          <div className="hidden sm:block h-8 w-px bg-gray-600"></div>
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Database className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
              <p className="text-lg sm:text-2xl font-bold text-white">Cloud</p>
            </div>
            <p className="text-xs sm:text-sm text-gray-400">Storage illimitato</p>
          </div>
        </div>
      </div>

      {/* Navigation Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 max-w-7xl mx-auto">
        {navigationCards.map((card) => (
          <div
            key={card.id}
            onClick={() => handleNavigation(card.route)}
            className={`group relative bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6 sm:p-8 cursor-pointer transition-all duration-300 hover:scale-105 hover:bg-gray-800/70 hover:border-gray-600 hover:shadow-2xl`}
          >
            {/* Gradient Background */}
            <div className={`absolute inset-0 bg-gradient-to-br ${card.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`}></div>
            
            {/* Content */}
            <div className="relative z-10">
              {/* Icon */}
              <div className={`inline-flex p-3 sm:p-4 bg-gradient-to-br ${card.color} rounded-2xl mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <card.icon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>

              {/* Title & Description */}
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3 group-hover:text-blue-400 transition-colors duration-300">
                {card.title}
              </h3>
              <p className="text-sm sm:text-base text-gray-400 mb-4 sm:mb-6 leading-relaxed">
                {card.description}
              </p>

              {/* Features */}
              <div className="space-y-2 mb-4 sm:mb-6">
                {card.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
                    <span className="text-xs sm:text-sm text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <button className={`w-full py-2 sm:py-3 px-4 sm:px-6 bg-gradient-to-r ${card.color} ${card.hoverColor} text-white font-medium rounded-xl transition-all duration-300 group-hover:shadow-lg transform group-hover:-translate-y-1 text-sm sm:text-base`}>
                Accedi a {card.title}
              </button>
            </div>

            {/* Hover Effect Border */}
            <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-blue-400/30 transition-colors duration-300"></div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="text-center mt-12 sm:mt-16">
        <div className="inline-flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-gray-800/30 rounded-full border border-gray-700">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span className="text-xs sm:text-sm text-gray-400">Sistema operativo e connesso</span>
        </div>
      </div>
    </div>
  );
};